import { useCallback, useSyncExternalStore } from 'react';
import { useAppSelector } from '@/store/hooks';

/**
 * Favourite menu pages, per user, kept in this browser (like Recent).
 * Only page ids are stored: the menu shows a favourite only while the page is still in the user's menu,
 * so a renamed page shows its new name and a page whose rights were removed disappears.
 */
const FAV_MAX = 15;
const key = (user?: string | null) => `erp.menuFav.${user ?? ''}`;
const listeners = new Set<() => void>();
const cache = new Map<string, string[]>();

const read = (user?: string | null): string[] => {
  const k = key(user);
  if (!cache.has(k)) {
    let ids: string[] = [];
    try {
      const v = JSON.parse(localStorage.getItem(k) ?? '[]');
      ids = Array.isArray(v) ? v.map(String).slice(0, FAV_MAX) : [];
    } catch {
      /* private window / storage blocked: no favourites */
    }
    cache.set(k, ids);
  }
  return cache.get(k) as string[];
};

const write = (user: string | null | undefined, ids: string[]) => {
  const next = ids.slice(0, FAV_MAX);
  cache.set(key(user), next);
  try {
    localStorage.setItem(key(user), JSON.stringify(next));
  } catch {
    /* kept for this session only */
  }
  listeners.forEach((l) => l());
};

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export const useMenuFavourites = () => {
  const user = useAppSelector((s) => s.loginReducer.loggedUserData?.user_code);
  const ids = useSyncExternalStore(subscribe, () => read(user));
  const toggle = useCallback(
    (id: string) => {
      const cur = read(user);
      write(user, cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
    },
    [user]
  );
  const move = useCallback(
    (id: string, by: number) => {
      const cur = [...read(user)];
      const i = cur.indexOf(id);
      const j = i + by;
      if (i < 0 || j < 0 || j >= cur.length) return;
      [cur[i], cur[j]] = [cur[j], cur[i]];
      write(user, cur);
    },
    [user]
  );
  return { ids, toggle, move, full: ids.length >= FAV_MAX, max: FAV_MAX };
};
